import { MouseEvent, useEffect, useState, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';

import { PhotoFieldsFragment } from 'api/graphql-types';
import Image from 'components/image';
import { Icon } from 'components/icon/icon';
import { calculateRatio } from 'utils/calculate-ratio';

import IconCross from 'public/assets/icons/cross.svg';
import IconChevronRight from 'public/assets/icons/chevron-right.svg';
import IconChevronLeft from 'public/assets/icons/chevron-left.svg';

import styles from './modal.module.scss';
import clsx from 'clsx';

export interface IModalProps {
	images?: PhotoFieldsFragment[] | PhotoFieldsFragment;
	activeItem?: number;
	onClose?: () => void;
}

const triggerResize = (): void => {
	if (typeof window !== 'undefined') {
		window.dispatchEvent(new Event('resize'));
	}
};

export const Modal = ({ images, activeItem, onClose }: IModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const headerRef = useRef<HTMLDivElement>(null);
	const footerRef = useRef<HTMLDivElement>(null);

	const [activeIndex, setActiveIndex] = useState<number>(activeItem || 0);

	const imagesIsArray = images && Array.isArray(images);
	const showNavigation = imagesIsArray && images.length > 1;
	const hasPrevious = imagesIsArray && activeIndex > 0;
	const hasNext = imagesIsArray && activeIndex < images.length - 1;
	const currentItem = imagesIsArray ? images[activeIndex] : images;
	const currentPhoto = currentItem?.photo.data?.attributes;
	const headerHeight = headerRef.current?.clientHeight || 0;
	const footerHeight = footerRef.current?.clientHeight || 0;
	const bodyHeight = `calc(100% - ${headerHeight + footerHeight}px)`;

	const aspectRatio = useMemo(() => {
		return calculateRatio(currentPhoto?.width, currentPhoto?.height);
	}, [currentPhoto?.width, currentPhoto?.height]);

	const onModalClose = (event: MouseEvent<HTMLDivElement | HTMLButtonElement>): void => {
		event.stopPropagation();
		if (onClose && event.target === event.currentTarget) onClose();
	};

	const handleEscapeKey = (event: KeyboardEvent): void => {
		if (event.key === 'Escape' && onClose) {
			onClose();
		}
	};

	const onPreviousClick = (): void => {
		if (hasPrevious) {
			setActiveIndex((previousValue) => previousValue - 1);
			triggerResize();
		}
	};

	const onNextClick = (): void => {
		if (hasNext) {
			setActiveIndex((previousValue) => previousValue + 1);
			triggerResize();
		}
	};

	useEffect(() => {
		if (typeof window === 'undefined' || !document.body) return;

		const scrollbarSize = window.innerWidth - document.documentElement.clientWidth;

		window.addEventListener('keydown', handleEscapeKey);
		document.body.style.overflow = 'hidden';
		document.body.style.paddingRight = `${scrollbarSize}px`;

		triggerResize();

		return (): void => {
			window.removeEventListener('keydown', handleEscapeKey);
			document.body.style.removeProperty('overflow');
			document.body.style.removeProperty('padding-right');
		};
	}, []);

	return ReactDOM.createPortal(
		<FocusLock autoFocus={false}>
			<div className={styles.wrapper} onClick={onModalClose}>
				<div className={styles.modal}>
					{showNavigation && (
						<div className={styles.navigation}>
							<button tabIndex={2} onClick={onPreviousClick} disabled={!hasPrevious}>
								<Icon customIcon={<IconChevronLeft />} size="16px" />
								<span>{formatMessage({ id: 'modules.photoAlbum.previous' })}</span>
							</button>
						</div>
					)}

					{currentItem && currentPhoto?.url && (
						<div key={`photo-dialog__${currentItem.id}`} className={styles.dialog}>
							<div ref={headerRef} className={styles.header}>
								<button
									tabIndex={1}
									onClick={() => {
										if (onClose) onClose();
									}}
								>
									<span>{formatMessage({ id: 'modules.photoAlbum.close' })}</span>
									<span className={styles.icon}>
										<Icon customIcon={<IconCross />} size="14px" />
									</span>
								</button>
							</div>

							<div className={clsx(styles.body, currentItem.description && styles.hasDescription)}>
								<div className={styles.photo} style={{ maxHeight: bodyHeight, aspectRatio }}>
									<Image
										fill
										priority
										attributes={{
											...currentPhoto,
											alternativeText: currentItem.title || currentPhoto?.alternativeText
										}}
									/>
								</div>

								{currentItem.description && (
									<div ref={footerRef} className={styles.description}>
										<div className={styles.content}>{currentItem.description}</div>
									</div>
								)}
							</div>
						</div>
					)}

					{showNavigation && (
						<div className={styles.navigation} onClick={onNextClick}>
							<button tabIndex={3} disabled={!hasNext}>
								<span>{formatMessage({ id: 'modules.photoAlbum.next' })}</span>
								<Icon customIcon={<IconChevronRight />} size="16px" />
							</button>
						</div>
					)}
				</div>
			</div>
		</FocusLock>,
		document.body
	);
};

export default Modal;
